import * as React from "react";
import {useSelector} from "react-redux";
import {CardContent, List, Typography} from "@material-ui/core";
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import {default as ViewAndDownloadDocument} from "./modals/ViewDocument";
import {CircularLoading} from "../../../../components/Common";
import useGetAllocations from "../../../../hooks/useGetAllocations";
import {CardSnapshot} from "./ClientSnapshot";
import {useActions} from "../../../../actions";
import {RootState} from "../../../../reducers";
import * as AccountActions from "../../../../actions/account";
import * as AwsActions from "../../../../actions/aws";
import * as EstimationActions from "../../../../actions/estimation";
import {getAllocationFromStressRequest, isNfIulCase} from "../../../../utils/commonFunc";
import { commonDownload, isDebug, maskCurrency, noop, pez, sleep, va, validObj } from "../../../../utils/commonUtils";
import { AccountType, CallingFromObject, ClientDocumentType, FileType } from "../../../../constants";
import { ANNUALLY, MONTHLY } from "../../Estimation/V1/Components/ContributionFrequency";

type BucketPathProps = { name?: string, path: string, enrollmentId: string };
const PictureAsPdfIconFC = ({ name, path, enrollmentId }: BucketPathProps) => {
    const debugMode = isDebug();
    const awsActions = useActions(AwsActions);
    const [loadingUrl, setLoadingUrl] = React.useState(false);
    const [url, setUrl] = React.useState("");


    const onDownload = (adminProposal: string) => {
        let file = adminProposal.split(".");
        let fileType = file[file.length - 1].toUpperCase();
        const contentType = (fileType == "PDF") ? ("text/pdf") : ("image/*");
        let queryObj = {
            objectName: pez(path),
            contentType: contentType,
        };
        return commonDownload(path, enrollmentId, queryObj, awsActions);
    };

    const getSignedUrl = async (adminProposal: string) => {
        let file = adminProposal.split(".");
        let fileType = file[file.length - 1].toUpperCase();
        const contentType = (fileType == "PDF") ? ("text/pdf") : ("image/*");

        let queryObj = {
            objectName: adminProposal,
            contentType: contentType,
        };
        try {
            const pdfFile = awsActions.getSecureUrl(enrollmentId, queryObj, true);
            if (!!pdfFile) {
                return pdfFile;
            }
            debugMode && console.info("Failed to get PDF file from AWS S3 with queryObj:", queryObj);
        } catch (error) {
            debugMode && console.error(error);
        }
    };

    const loadPdf: any = async () => {
        setLoadingUrl(true);
        debugMode && await sleep(5000); // Simulate loading delay
        const fileUrl = await getSignedUrl(pez(path));
        setLoadingUrl(false);
        debugMode && console.info("Loading PDF from bucket path:", name, path, "URL:", fileUrl);
        setUrl(fileUrl);
    };

    return (
            <div className="colorBlue cursor">
                {
                        loadingUrl &&
                        <CircularLoading/>
                }

                <div className="floatLeft textCenter w50" onClick={loadPdf}>
                    <div className="colorBlue cursor">
                        <PictureAsPdfIcon className="colorBlue cursor" onClick={!loadingUrl ? loadPdf : noop}/>
                    </div>
                    <Typography variant="body2" className="colorBlue">{name}</Typography>
                </div>

                <ViewAndDownloadDocument
                        documentUrl={url}
                        downloadDocument={() => onDownload(pez(path))}
                        fileType={FileType.PDF}
                        handleClose={() => setUrl("")}
                        openDocument={!!url}
                        title={name}
                />
            </div>
    )
};

const ProposalSummaryPdfs = ({ documents, enrollmentId }: {
    documents: any,
    enrollmentId: string
}) => {
    return (
            <>
                {
                        documents && documents.map((data, index) => (
                                <PictureAsPdfIconFC {...data} key={index} enrollmentId={enrollmentId} />
                        ))
                }
            </>
    );
};

const ProposalSummary = ({forceRefreshSummary}: { forceRefreshSummary: number }) => {
    const debugMode = isDebug();
    const estimationActions = useActions(EstimationActions);
    const { getClientDocumentByType } = useActions(AccountActions);
    const [loadingEstimationData, setLoadingEstimationData] = React.useState<boolean>(false);
    const [loadingReportData, setLoadingReportData] = React.useState<boolean>(false);
    const [documents, setDocuments] = React.useState<any>([]);
    const [estimationResponse, setEstimationResponse] = React.useState<any>([]);
    const [estimationDetails, setEstimationDetails] = React.useState<any>([]);
    const callingFrom: string = CallingFromObject.AGENT_ADMIN_ILLUSTRATION;
    const clientDetails: any = useSelector((state: RootState) => state.clientAccount);
    const profile = useSelector((state: RootState) => state.profile);
    const userRole = (profile.role === AccountType.Individual || profile.role === AccountType.Multilife);

    let clientPolicy = clientDetails && clientDetails.client && clientDetails.client.clientPolicies && clientDetails.client.clientPolicies[0];
    if (userRole) {
        clientPolicy = profile && profile.client && profile.client.clientPolicies && profile.client.clientPolicies.length > 0 && profile.client.clientPolicies[0];
    }
    const carrierId = clientPolicy && clientPolicy.carrierId;
    const strategyId = clientPolicy && clientPolicy.strategyId;
    const strategySlug: string = clientPolicy && clientPolicy.strategy && clientPolicy.strategy.slug ? clientPolicy.strategy.slug : "";
    const isNfIul = isNfIulCase((clientPolicy && clientPolicy.strategy && clientPolicy.strategy.id) as number, strategySlug);
    const allocationQuestion = useGetAllocations(carrierId, strategyId);
    const allocationFromStressRequest = allocationQuestion && validObj(allocationQuestion) && estimationDetails && estimationDetails.stressRequest ? getAllocationFromStressRequest(allocationQuestion, estimationDetails) : [];
    const summaryIndexOptions: any[] = va(allocationFromStressRequest) ?
            allocationFromStressRequest.map(({label, weight}) => ({label, value: `${weight}%`})) :
            [];
    // const contributionFrequency = (clientPolicy && clientPolicy.paymentFrequency) === "monthly" ? MONTHLY : ANNUALLY;
    const {common, illustration, stress} = estimationResponse;
    const incomeAgeTo: number = (common && common.incomeageto) || 0;
    const incomeAgeFrom: number = (common && common.incomeagefrom) || 0;
    const deathBenefitLessLoanYear1 = (illustration && illustration.data && illustration.data.deathbenefitlessloanyear1) || 0;
    const paymentFrequency = (common && common.paymentfrequency) || "Annual";
    const contributionAmountEmployer = (common && common.contributionamountemployer) || 0;
    const contributionAmountIncEmployer = (common && common.contributionamount) || 0;
    let contributionAmount:any = contributionAmountIncEmployer - contributionAmountEmployer;

    if (isNfIul) {
        contributionAmount = paymentFrequency === MONTHLY ? contributionAmountIncEmployer * 12 : contributionAmountIncEmployer;
    }

    const lenderContributionYear1 = (illustration && illustration.data && illustration.data.loanamountyear1) || 0;
    const totalPremium = common && common.premium;
    const illustratedAnnualIncome = illustration && illustration.income;

    const summaryDataItems: any[] = [
        {label: "Initial Death Benefit", value: maskCurrency(deathBenefitLessLoanYear1)},
        {label: "Client Contribution (includes trust fee)", value: maskCurrency(contributionAmount.toString())},
        {label: "Employer Contribution", value: maskCurrency(contributionAmountEmployer)},
        {label: "Bank Contribution (Years 1-5)", value: maskCurrency(lenderContributionYear1)},
        {label: "Total Premium", value: maskCurrency(totalPremium)},
        {label: "Client payment frequency", value: paymentFrequency},
        {label: "Income Start Age", value: incomeAgeFrom},
        {label: "Income End Age", value: incomeAgeTo},
        {label: "Illustrated Annual Income", value: maskCurrency(illustratedAnnualIncome)},
        {label: "Index Options:", icon: true},
    ].concat(summaryIndexOptions);

    const getClientEstimationData = async () => {
        if (!clientDetails && !userRole) {
            debugMode && console.error("No client detail found.");
            return;
        }

        const userId = userRole ? profile && profile.client && profile.client.userId : clientDetails.id;
        const clientPolicyId = clientPolicy && clientPolicy.id;
        setLoadingEstimationData(true);
        debugMode && await sleep(3000);

        const estimationDetails: any = await estimationActions.getEstimationDetailsByUserId(userId, callingFrom, clientPolicyId);
        if (!estimationDetails || !estimationDetails.id) {
            setLoadingEstimationData(false);
            debugMode && console.error("No estimation details found for userId:", userId, "and clientPolicyId:", clientPolicyId);
            return;
        }

        debugMode && console.log(estimationDetails && estimationDetails.id, "estimationDetails");

        if (!estimationDetails.stressResponse) {
            setLoadingEstimationData(false);
            debugMode && console.error("No stress response found in estimation details for userId:", userId, "and clientPolicyId:", clientPolicyId);
            return;
        }

        const stressResponse: any = JSON.parse(estimationDetails && estimationDetails.stressResponse);

        if (!stressResponse && !stressResponse.success) {
            setLoadingEstimationData(false);
            debugMode && console.error("No success response found in estimation details for userId:", userId, "and clientPolicyId:", clientPolicyId);
            return;
        }

        debugMode && console.log(estimationDetails, "responsePayload");
        debugMode && console.log(stressResponse, "responsePayload2");
        setEstimationDetails(estimationDetails);
        setEstimationResponse(stressResponse && stressResponse.success);
        setLoadingEstimationData(false);
    };
    const getEstimationFiles = async () => {
        if (!clientDetails && !userRole) {
            debugMode && console.error("No client detail found.");
            return;
        }

        setLoadingReportData(true);
        debugMode && await sleep(3000);
        setLoadingReportData(false);
    };

    const pageLoadEffect = () => {
        if (forceRefreshSummary === 0) {
            return;
        }
        debugMode && console.info('first load summary data', forceRefreshSummary);
        getClientEstimationData().then(getEstimationFiles);
    };

    React.useEffect(pageLoadEffect, [forceRefreshSummary]);

    debugMode && console.info({allocationFromStressRequest, allocationQuestion})

    React.useEffect(() => {
        (
                async () => {
                    if (clientPolicy && clientPolicy.id) {
                        const docs = await getClientDocumentByType(clientPolicy.id, ClientDocumentType.ADMIN_PROPOSAL, isNfIul);
                        setDocuments(docs);
                    }
                }
        )();
    }, []);

    return (
            <>
                <CardSnapshot title="Proposal Summary">
                    <CardContent className="floatLeft w100">
                        <List className="ClientDetailsList w100">
                            { !isNfIul &&
                                <>
                                    {
                                            loadingEstimationData &&
                                            <li>
                                                <label>Loading Proposal Summary...</label>
                                            </li>
                                    }
                                    {
                                            !loadingEstimationData &&
                                            summaryDataItems.map((item, idx) => (
                                                    <li key={idx}>
                                                        <label>{item.label}</label>
                                                        <span className="labelValue">
                                                            {item.icon ? <ArrowDownwardIcon/> : item.value}
                                                        </span>
                                                    </li>
                                            ))
                                    }
                                    <li>
                                        <label className="hLine"/>
                                    </li>
                                    {
                                            loadingReportData &&
                                            <li>
                                                <label>Loading Reports...</label>
                                            </li>
                                    }
                                </>
                            }
                            <li>
                                {
                                        !loadingReportData &&
                                        <ProposalSummaryPdfs documents={documents}
                                                             enrollmentId={clientPolicy && clientPolicy.enrollment && clientPolicy.enrollment.id} />
                                }
                            </li>
                        </List>
                    </CardContent>
                </CardSnapshot>
            </>
    );
};

export default ProposalSummary;
